import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Check, Loader2, X } from "lucide-react";
import {
  useAcceptInvitation,
  useMyInvitations,
  useRejectInvitation,
} from "@/hooks/use-groups";
import { GroupCapInquiryDialog } from "@/components/group-cap-inquiry-dialog";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ApiError } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";

// v1: 그룹 초대 한 종류만 다루지만, 향후 다른 이벤트(시험 결과 공지 등)
// 가 추가될 때 같은 popover에 합치기 쉽도록 항목을 한 번 정규화한다.
type NotificationItem = {
  kind: "group_invite";
  groupId: string;
  groupName: string;
  expiresAt: string;
};

export function NotificationPopover() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: invitations = [], isLoading } = useMyInvitations();
  const accept = useAcceptInvitation();
  const reject = useRejectInvitation();

  const [capDialog, setCapDialog] = useState<{
    gid: string;
    name: string;
  } | null>(null);
  const [errorByGid, setErrorByGid] = useState<Record<string, string>>({});

  const items: NotificationItem[] = invitations.map((inv) => ({
    kind: "group_invite",
    groupId: inv.group_id,
    groupName: inv.group_name || "그룹",
    expiresAt: inv.expires_at,
  }));
  const count = items.length;

  async function handleAccept(item: NotificationItem) {
    setErrorByGid((m) => ({ ...m, [item.groupId]: "" }));
    try {
      await accept.mutateAsync(item.groupId);
      setOpen(false);
      navigate({ to: "/groups/$gid", params: { gid: item.groupId } });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setCapDialog({ gid: item.groupId, name: item.groupName });
        return;
      }
      setErrorByGid((m) => ({
        ...m,
        [item.groupId]: createErrorMessage(err),
      }));
    }
  }

  async function handleReject(item: NotificationItem) {
    setErrorByGid((m) => ({ ...m, [item.groupId]: "" }));
    try {
      await reject.mutateAsync(item.groupId);
    } catch (err) {
      setErrorByGid((m) => ({
        ...m,
        [item.groupId]: createErrorMessage(err),
      }));
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label={count > 0 ? `알림 ${count}건` : "알림"}
          className="relative cursor-pointer rounded-md p-2 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Bell className="size-5" />
          {count > 0 && (
            <span className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {count}
            </span>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0">
          <div className="border-b px-3 py-2 text-sm font-semibold">알림</div>
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              받은 알림이 없습니다
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((item) => (
                <NotificationRow
                  key={`${item.kind}:${item.groupId}`}
                  item={item}
                  error={errorByGid[item.groupId]}
                  isAccepting={accept.isPending}
                  isRejecting={reject.isPending}
                  onAccept={() => handleAccept(item)}
                  onReject={() => handleReject(item)}
                />
              ))}
            </ul>
          )}
        </PopoverContent>
      </Popover>

      {capDialog && (
        <GroupCapInquiryDialog
          open={!!capDialog}
          onOpenChange={(v) => !v && setCapDialog(null)}
          groupId={capDialog.gid}
          groupName={capDialog.name}
        />
      )}
    </>
  );
}

function NotificationRow({
  item,
  error,
  isAccepting,
  isRejecting,
  onAccept,
  onReject,
}: {
  item: NotificationItem;
  error?: string;
  isAccepting: boolean;
  isRejecting: boolean;
  onAccept: () => void;
  onReject: () => void;
}) {
  const expiresAt = new Date(item.expiresAt);
  const daysLeft = Math.max(
    0,
    Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );
  return (
    <li className="flex flex-col gap-2 border-b px-3 py-3 last:border-b-0">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium">{item.groupName} 그룹 초대</p>
        <p className="text-xs text-muted-foreground">
          {daysLeft > 0 ? `${daysLeft}일 후 만료` : "오늘 만료 예정"}
        </p>
      </div>
      {error && (
        <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onReject}
          disabled={isRejecting}
        >
          <X className="size-4" />
          거부
        </Button>
        <Button size="sm" onClick={onAccept} disabled={isAccepting}>
          <Check className="size-4" />
          승인
        </Button>
      </div>
    </li>
  );
}
